import { NetSuiteTable } from './NetSuiteTable';
import axios from 'axios';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5001').replace(/\/api$/, '');

interface FilteredNetSuiteTableProps {
  filter: 'all' | 'kitting' | 'fabric' | 'metal' | 'assembly';
}

// Interface for the update data that will be sent to NetSuite
interface NetSuiteUpdateData {
  lineIndex: string;
  woId?: string;
  kittingComplete?: boolean;
  fabricComplete?: boolean;
  metalComplete?: boolean;
  finalAssemblyComplete?: boolean;
  isWorkOrder?: boolean;
  isReady?: boolean;
  isTKS0026?: boolean;
}

// Use the DisplayItem type from NetSuiteTable to avoid conflicts
import { DisplayItem } from './NetSuiteTable';

export function FilteredNetSuiteTable({ filter }: FilteredNetSuiteTableProps) {
  const { toast } = useToast();
  const { token } = useAuth();

  const handleRowClick = async (row: DisplayItem) => {
    try {
      console.log('🔄 Handling row click for:', row);
      // Determine what to update based on row type and current filter
      const updateData: NetSuiteUpdateData = {
        lineIndex: row.lineIndex,
      };

      // Handle work orders
      if (row.isWorkOrder) {
        // Initialize with current values from the row when available
        const kittingComplete = row.kittingComplete || false;
        const fabricComplete = row.fabricComplete || false;
        const metalComplete = row.metalComplete || false;
        const finalAssemblyComplete = row.finalAssemblyComplete || false;
        const isTKS0026 = row.isTKS0026 || false;

        // Set the appropriate field based on the filter
        switch (filter) {
          case 'kitting':
            Object.assign(updateData, {
              woId: row.woId,
              isWorkOrder: true,
              isTKS0026: isTKS0026,
              kittingComplete: true,
              fabricComplete: fabricComplete,
              metalComplete: metalComplete,
              finalAssemblyComplete: finalAssemblyComplete,
            });
            break;
          case 'fabric':
            Object.assign(updateData, {
              woId: row.woId,
              isWorkOrder: true,
              isTKS0026: isTKS0026,
              kittingComplete: true, // Should already be true
              fabricComplete: true,
              metalComplete: metalComplete,
              finalAssemblyComplete: finalAssemblyComplete,
            });
            break;
          case 'metal':
            Object.assign(updateData, {
              woId: row.woId,
              isWorkOrder: true,
              isTKS0026: isTKS0026,
              kittingComplete: true, // Should already be true
              fabricComplete: fabricComplete,
              metalComplete: true,
              finalAssemblyComplete: finalAssemblyComplete,
            });
            break;
          case 'assembly':
            Object.assign(updateData, {
              woId: row.woId,
              isWorkOrder: true,
              isTKS0026: isTKS0026,
              kittingComplete: true, // Should already be true
              fabricComplete: true, // Should already be true
              metalComplete: true, // Should already be true
              finalAssemblyComplete: true,
            });
            break;
          default:
            Object.assign(updateData, {
              woId: row.woId,
              isWorkOrder: true,
              isTKS0026: isTKS0026,
              kittingComplete: kittingComplete,
              fabricComplete: fabricComplete,
              metalComplete: metalComplete,
              finalAssemblyComplete: finalAssemblyComplete,
            });
        }

        // Set isReady flag based on completion statuses, with special handling for TKS-0026 items
        if (isTKS0026) {
          // For TKS-0026 items, only metal and final assembly completion are required
          updateData.isReady = updateData.metalComplete && updateData.finalAssemblyComplete;
        } else {
          // For regular items, all four phases are required
          updateData.isReady = updateData.kittingComplete && 
                              updateData.fabricComplete && 
                              updateData.metalComplete && 
                              updateData.finalAssemblyComplete;
        }
      } 
      // Handle non-work order items (only have kitting status)
      else {
        // For non-work order items, we only set isReady to true
        // In the backend, this will set the line item to "ready"
        updateData.isWorkOrder = false;
        updateData.kittingComplete = true;
        updateData.isReady = true;

        console.log('🔄 Non-work order item update data:', {
          id: row.id,
          salesOrderId: row.salesOrderId,
          orderID: row.orderID,
          lineIndex: row.lineIndex,
          isWorkOrder: false,
          isReady: true,
          updateData
        });
      }

      // Send update to the API
      // Note that we're using salesOrderId directly as it's the correct internal ID
      const orderId = row.salesOrderId;
      
      console.log(`🔄 Sending update for order ${orderId} with data:`, updateData);
      
      await axios.put(`${API_URL}/api/netsuite/data`, {
        id: orderId, // ID of the parent Sales Order
        ...updateData
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast({
        title: 'Success',
        description: 'Successfully updated status',
      });
    } catch (error) {
      console.error('Error updating row:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const filterData = (data: any[]) => {
    console.log('🔍 FilteredNetSuiteTable filtering data:', data);
    
    // Add computed fields for display
    const addDetails = (row: any) => ({
      ...row,
    });

    switch (filter) {
      case 'kitting':
        // Show both work orders and non-work order items that need kitting, exclude TKS-0026 items
        return data.filter((row) => 
          (!row.kittingComplete && !row.isTKS0026) || // Regular items that need kitting
          (!row.isWorkOrder && !row.kittingComplete) // Non-work order items
        );
      
      case 'fabric':
        // Only show non-TKS-0026 work orders that need fabric work and have completed kitting
        return data.filter((row) => 
          row.isWorkOrder && !row.fabricComplete && row.kittingComplete && !row.isTKS0026
        ).map(row => addDetails(row));
      
      case 'metal':
        // Show both TKS-0026 and regular work orders that need metal work
        // For TKS-0026, they appear in metal without any prerequisites
        // For regular work orders, they need completed kitting
        return data.filter((row) => 
          (row.isWorkOrder && !row.metalComplete && row.isTKS0026) || // TKS-0026 items
          (row.isWorkOrder && !row.metalComplete && row.kittingComplete && !row.isTKS0026) // Regular items
        ).map(row => addDetails(row));
      
      case 'assembly':
        // Show both TKS-0026 and regular work orders that need final assembly
        // For TKS-0026, they need completed metal
        // For regular items, they need completed kitting, fabric, and metal
        return data.filter((row) => 
          (row.isWorkOrder && !row.finalAssemblyComplete && row.metalComplete && row.isTKS0026) || // TKS-0026 items
          (row.isWorkOrder && !row.finalAssemblyComplete && row.kittingComplete && row.fabricComplete && row.metalComplete && !row.isTKS0026) // Regular items
        );
      
      default:
        // Show all items
        return data.map(row => addDetails(row));
    }
  };

  return <NetSuiteTable 
    filterFn={filterData} 
    onRowClick={filter === 'all' ? undefined : handleRowClick} 
    currentFilter={filter}
  />;
} 